import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import communityService from "../../services/communityService";
import ExplorerTab from "../../components/community/ExplorerTab";
import FollowingTab from "../../components/community/FollowingTab";
import LeaderboardTab from "../../components/community/LeaderboardTab";
import BadgesTab from "../../components/community/BadgesTab";
import { glassPanel } from "../../components/layout/UserLayout/designTokens";
import {
  HiOutlineUserGroup,
  HiOutlineMagnifyingGlass,
  HiOutlineTrophy,
  HiOutlineSparkles,
  HiOutlineUsers,
  HiOutlineBookOpen,
  HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";

/**
 * Page principale de la Communauté
 * Espace social avec onglets pour découvrir, suivre et interagir
 */
const CommunityPage = ({ user, token }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("explorer");
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger les statistiques de la communauté
  useEffect(() => {
    const loadStats = async () => {
      try {
        const stats = await communityService.getCommunityStats();
        setCommunityStats(stats);
      } catch (error) {
        console.error("Erreur lors du chargement des statistiques:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const iconClass = "w-5 h-5 shrink-0";
  const tabs = [
    {
      id: "explorer",
      label: t("explore"),
      icon: <HiOutlineMagnifyingGlass className={iconClass} />,
    },
    {
      id: "following",
      label: t("following"),
      icon: <HiOutlineUsers className={iconClass} />,
    },
    {
      id: "leaderboard",
      label: t("leaderboard"),
      icon: <HiOutlineTrophy className={iconClass} />,
    },
    {
      id: "badges",
      label: t("badges"),
      icon: <HiOutlineSparkles className={iconClass} />,
    },
  ];

  return (
    <div className="min-h-full py-3 sm:py-6">
      {/* En-tête de la page - design glass, adapté mobile */}
      <div
        className={`relative ${glassPanel} rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 mb-4 sm:mb-8 overflow-hidden`}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-1 sm:w-2 bg-gradient-to-b from-orange-500 to-amber-500 rounded-r-full"
          aria-hidden
        />
        <div className="pl-3 sm:pl-5 lg:pl-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white flex items-center gap-2 sm:gap-3 tracking-tight">
                <HiOutlineUserGroup
                  className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 text-amber-400 shrink-0"
                  aria-hidden
                />
                <span className="truncate">{t("community")}</span>
              </h1>
              <p className="mt-1 text-white/90 text-xs sm:text-sm lg:text-base">
                {t("communitySubtitle")}
              </p>
            </div>

            {/* Statistiques - compact sur mobile, détaillé sur desktop */}
            {!loading && communityStats && (
              <>
                <div className="flex lg:hidden items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-1.5">
                    <HiOutlineUsers className="w-4 h-4 text-orange-400/90 shrink-0" aria-hidden />
                    <span className="text-base font-bold text-orange-400 tabular-nums">
                      {communityStats.active_users || 0}
                    </span>
                    <span className="text-xs text-white/80">{t("cooks")}</span>
                  </div>
                  <div className="w-px h-5 bg-white/20" aria-hidden />
                  <div className="flex items-center gap-1.5">
                    <HiOutlineBookOpen className="w-4 h-4 text-amber-400/90 shrink-0" aria-hidden />
                    <span className="text-base font-bold text-amber-400 tabular-nums">
                      {communityStats.shared_recipes || 0}
                    </span>
                    <span className="text-xs text-white/80">{t("recipes")}</span>
                  </div>
                  <div className="w-px h-5 bg-white/20" aria-hidden />
                  <div className="flex items-center gap-1.5">
                    <HiOutlineChatBubbleLeftRight className="w-4 h-4 text-orange-300/90 shrink-0" aria-hidden />
                    <span className="text-base font-bold text-orange-300 tabular-nums">
                      {communityStats.total_comments || 0}
                    </span>
                    <span className="text-xs text-white/80">{t("comments")}</span>
                  </div>
                </div>
                <div className="hidden lg:flex items-center gap-6">
                  <div className="flex flex-col items-center gap-1">
                    <HiOutlineUsers className="w-5 h-5 text-orange-400/90 shrink-0" aria-hidden />
                    <div className="text-2xl font-bold text-orange-400">{communityStats.active_users || 0}</div>
                    <div className="text-xs text-white/90 font-medium">{t("cooks")}</div>
                  </div>
                  <div className="w-px h-12 bg-white/20" aria-hidden />
                  <div className="flex flex-col items-center gap-1">
                    <HiOutlineBookOpen className="w-5 h-5 text-amber-400/90 shrink-0" aria-hidden />
                    <div className="text-2xl font-bold text-amber-400">{communityStats.shared_recipes || 0}</div>
                    <div className="text-xs text-white/90 font-medium">{t("recipes")}</div>
                  </div>
                  <div className="w-px h-12 bg-white/20" aria-hidden />
                  <div className="flex flex-col items-center gap-1">
                    <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-orange-300/90 shrink-0" aria-hidden />
                    <div className="text-2xl font-bold text-orange-300">{communityStats.total_comments || 0}</div>
                    <div className="text-xs text-white/90 font-medium">{t("comments")}</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Onglets - scroll horizontal sur mobile, touch-friendly */}
          <div className="mt-4 sm:mt-6 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent touch-pan-x">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium text-sm sm:text-base transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md hover:from-orange-600 hover:to-amber-600"
                    : "bg-white/10 text-white border border-white/20 hover:bg-white/20"
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
        {activeTab === "explorer" && <ExplorerTab user={user} token={token} />}
        {activeTab === "following" && (
          <FollowingTab user={user} token={token} />
        )}
        {activeTab === "leaderboard" && (
          <LeaderboardTab user={user} token={token} />
        )}
        {activeTab === "badges" && <BadgesTab user={user} token={token} />}
      </div>
    </div>
  );
};

export default CommunityPage;
